// Default content selection for the Analytics dashboard when the URL carries
// no explicit ids: the most recent items in the active scope, capped at the
// selection limit. Live streams come straight from the Streams store; VOD
// files have no flat "all files" endpoint, so the most recently touched
// folders are enumerated (playlist per folder, in parallel, small cap) and
// cached for the session. None of this touches Bitmovin.
import StreamService from '@/services/StreamService';
import { vodFileKey } from './analyticsContentIndex';

export const MAX_DEFAULT_FOLDERS = 5;

const byNewest = (field) => (a, b) => new Date(b[field] || 0) - new Date(a[field] || 0);
const touchedAt = (folder) => folder.updatedAt || folder.lastUpdated || folder.creationTime || 0;

// folderId -> Promise<files[]> (session cache; the picker refreshes independently)
const playlistCache = new Map();

export function clearDefaultSelectionCache() {
	playlistCache.clear();
}

async function loadPlaylist(folder) {
	try {
		const files = await StreamService.getStreamPlaylist(folder._id);
		return (Array.isArray(files) ? files : []).map((f) => ({ ...f, playlistName: folder.name, playlistId: folder._id }));
	} catch (e) {
		playlistCache.delete(folder._id); // do not cache a failure
		return [];
	}
}

function fetchPlaylist(folder) {
	if (!playlistCache.has(folder._id)) playlistCache.set(folder._id, loadPlaylist(folder));
	return playlistCache.get(folder._id);
}

/**
 * Most recent live streams by creation time.
 * @returns {Array} store stream objects
 */
export function recentLiveStreams(liveStreams = [], limit = 10) {
	return liveStreams
		.filter((s) => s && s.key)
		.slice()
		.sort(byNewest('creationTime'))
		.slice(0, limit);
}

/**
 * Pick the folders worth enumerating: non-empty, most recently touched first.
 */
export function pickRecentFolders(vodFolders = [], cap = MAX_DEFAULT_FOLDERS) {
	return vodFolders
		.filter((f) => f && f._id && f.totalFiles !== 0)
		.slice()
		.sort((a, b) => new Date(touchedAt(b)) - new Date(touchedAt(a)))
		.slice(0, cap);
}

/**
 * Most recent VOD files across the most recent folders.
 * Cost: one GET /streams/:id/schedular/playlist per folder (max `cap`), cached.
 * @returns {Promise<Array>} files with playlistName/playlistId attached
 */
export async function recentVodFiles(vodFolders = [], { limit = 10, cap = MAX_DEFAULT_FOLDERS } = {}) {
	const folders = pickRecentFolders(vodFolders, cap);
	if (!folders.length) return [];
	const lists = await Promise.all(folders.map(fetchPlaylist));
	const seen = new Set();
	return lists
		.flat()
		.filter((f) => {
			const key = vodFileKey(f);
			if (!key || seen.has(key)) return false;
			seen.add(key);
			return true;
		})
		.sort(byNewest('creationTime'))
		.slice(0, limit);
}

/**
 * Locate the VOD files behind a set of refIds (e.g. `ids` restored from the
 * URL) so their name, type and folder can be shown. Folders named in
 * `folderIds` (the URL hint written alongside `ids`) are read first — one
 * cached playlist request each — then, for anything still missing, the most
 * recently touched folders are swept in small batches up to `maxFolders`.
 * Castr API only; nothing here touches Bitmovin.
 * @returns {Promise<Array>} matched files with playlistName/playlistId attached
 */
export async function findVodFiles(ids = [], vodFolders = [], { folderIds = [], maxFolders = 20 } = {}) {
	const wanted = new Set(ids.filter(Boolean));
	const found = [];
	if (!wanted.size) return found;
	const take = (lists) => {
		for (const f of lists.flat()) {
			const key = vodFileKey(f);
			if (wanted.has(key)) {
				wanted.delete(key);
				found.push(f);
			}
		}
	};
	const byId = new Map(vodFolders.filter((f) => f && f._id).map((f) => [String(f._id), f]));
	const hinted = folderIds.map((id) => byId.get(String(id))).filter(Boolean);
	if (hinted.length) take(await Promise.all(hinted.map(fetchPlaylist)));
	if (!wanted.size) return found;
	const rest = pickRecentFolders(vodFolders, Infinity).filter((f) => !hinted.includes(f)).slice(0, maxFolders);
	for (let i = 0; i < rest.length && wanted.size; i += MAX_DEFAULT_FOLDERS) {
		take(await Promise.all(rest.slice(i, i + MAX_DEFAULT_FOLDERS).map(fetchPlaylist)));
	}
	return found;
}

/**
 * Build the default selection for a scope.
 *  - live: `limit` most recent live streams
 *  - vod:  `limit` most recent VOD files
 *  - all:  half live + half VOD, each side topping up when the other is short
 * @returns {Promise<{ ids: string[], liveCount: number, vodCount: number, vodFiles: Array }>}
 */
export async function buildDefaultSelection({ scope = 'all', liveStreams = [], vodFolders = [], limit = 10 } = {}) {
	if (scope === 'live') {
		const live = recentLiveStreams(liveStreams, limit);
		return { ids: live.map((s) => String(s.key)), liveCount: live.length, vodCount: 0, vodFiles: [] };
	}
	if (scope === 'vod') {
		const files = await recentVodFiles(vodFolders, { limit });
		return { ids: files.map(vodFileKey), liveCount: 0, vodCount: files.length, vodFiles: files };
	}
	const half = Math.ceil(limit / 2);
	const liveAll = recentLiveStreams(liveStreams, limit);
	const vodAll = await recentVodFiles(vodFolders, { limit });
	// Half each; whichever side is short hands its slots to the other.
	const vodTake = Math.min(limit - Math.min(half, liveAll.length), vodAll.length);
	const liveTake = Math.min(limit - vodTake, liveAll.length);
	const live = liveAll.slice(0, liveTake);
	const files = vodAll.slice(0, vodTake);
	return {
		ids: [...live.map((s) => String(s.key)), ...files.map(vodFileKey)],
		liveCount: live.length,
		vodCount: files.length,
		vodFiles: files,
	};
}

/**
 * "10 most recent items" / "5 live streams and 5 videos" / "3 videos"
 */
export function describeDefaultSelection({ liveCount = 0, vodCount = 0 } = {}) {
	const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
	if (liveCount && vodCount) return `your ${plural(liveCount, 'live stream', 'live streams')} and ${plural(vodCount, 'video', 'videos')}`;
	if (liveCount) return `your ${plural(liveCount, 'most recent live stream', 'most recent live streams')}`;
	if (vodCount) return `your ${plural(vodCount, 'most recent video', 'most recent videos')}`;
	return '';
}
